import { memo, useId, useMemo, type MouseEventHandler, type ReactNode, type FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import isString from 'lodash/isString';
// Material UI imports
import type { BoxProps, SxProps, Theme } from '@mui/material';
import Typography from '@mui/material/Typography';
import Button, { type ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog, { type DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
// local imports
import BoxTypography from '../mixins/BoxTypography';
import { Values } from '../helpers/intl';
import CloseIconButton from '../elements/CloseIconButton';
// SCSS imports
import { actions, actionsPadding, button, cancelButton, extraPadding } from './ConfirmDialog.module.scss';

const paperProps = { className: extraPadding };
const contentSx: SxProps<Theme> = { pb: 1 } as const;
const textPaddingProp: BoxProps['px'] = { sm: 4 } as const;

type ConfirmDialogProps = {
  readonly open: boolean;
  readonly maxWidth?: DialogProps['maxWidth'];
  readonly title?: ReactNode | string | null;
  readonly text?: ReactNode | string | null;
  readonly values?: Values;
  readonly withCancelButton?: boolean;
  readonly withExtraPadding?: boolean;
  readonly withoutCloseButton?: boolean;
  readonly pending?: boolean;
  readonly confirmPending?: boolean;
  readonly cancelColor?: ButtonProps['color'];
  readonly confirmColor?: ButtonProps['color'];
  readonly cancelLabel?: string | null;
  readonly confirmLabel?: string | null;
  readonly onCancel: MouseEventHandler<HTMLButtonElement>;
  readonly onConfirm?: MouseEventHandler<HTMLButtonElement>;
  readonly onExited: (node: HTMLElement) => void;
};

const ConfirmDialog: FunctionComponent<ConfirmDialogProps> = ({
  open = false,
  maxWidth,
  title,
  text,
  values,
  withCancelButton = false,
  withExtraPadding = false,
  withoutCloseButton = false,
  pending,
  confirmPending,
  cancelColor,
  confirmColor,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
  onExited
}) => {
  // Per instance, since two dialogs can be mounted at once, and stable across renders.
  const textId = `${useId()}-text`;

  const [labelledBy, describedBy] = useMemo(() => [
    !title && text ? textId : undefined,
    title && text ? textId : undefined
  ], [title, text, textId]);

  const titleSx: SxProps<Theme> = useMemo(() => ({ pt: 3.5, pb: text ? 2 : 3.5, px: 7, textAlign: 'center' }), [text]);

  const [handleClose, confirmIcon, confirmDisabled] = useMemo(
    (): [DialogProps['onClose'], ReactNode, boolean | undefined] => [
      pending ? undefined : onCancel,
      pending || confirmPending ? <CircularProgress size="1rem" color="inherit"/> : undefined,
      pending || confirmPending
    ], [pending, confirmPending, onCancel]
  );

  const slotProps = useMemo(() => ({
    transition: { onExited },
    paper: withExtraPadding ? paperProps : undefined
  } as DialogProps['slotProps']), [onExited, withExtraPadding]);

  return (
    <Dialog
        maxWidth={maxWidth}
        open={open}
        onClose={handleClose}
        scroll="body"
        slotProps={slotProps}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
    >
      {!withoutCloseButton && <CloseIconButton small onClick={onCancel} disabled={pending}/>}
      {title ? (
        <DialogTitle sx={titleSx}>
          <Typography variant="subtitle1" component="span">
            {isString(title)
              ? <FormattedMessage id={title} defaultMessage={title} values={values}/>
              : title }
          </Typography>
        </DialogTitle>
      ) : undefined}
      {text ? (
        <DialogContent id={textId} sx={contentSx}>
          <BoxTypography pt={title ? undefined : 1} pb={2.5} px={textPaddingProp} variant="body1" textAlign="center">
            {isString(text)
              ? <FormattedMessage id={text} defaultMessage={text} values={values}/>
              : text }
          </BoxTypography>
        </DialogContent>
      ) : undefined}
      {onConfirm || withCancelButton ? (
        <DialogActions className={withExtraPadding ? actionsPadding : actions}>
          {withCancelButton ? (
            <Button
                onClick={onCancel}
                color={cancelColor || 'primary'}
                size="small"
                variant="outlined"
                disabled={pending}
                className={cancelButton}
            >
              <FormattedMessage id={cancelLabel || 'common.button.cancel'}/>
            </Button>
          ) : undefined}
          {onConfirm ? (
            <Button
                onClick={onConfirm}
                color={confirmColor || 'primary'}
                size="small"
                variant="contained"
                disableElevation
                autoFocus
                disabled={confirmDisabled}
                startIcon={confirmIcon}
                className={button}
            >
              <FormattedMessage id={confirmLabel || 'common.button.confirm'}/>
            </Button>
          ) : undefined}
        </DialogActions>
      ) : undefined}
    </Dialog>
  );
};

export default memo(ConfirmDialog);
